import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { clampToViewport } from '../../utils/clampToViewport';
import useLocalize from '../../utils/hooks/useLocalize';
import './DeleteConfirmModal.scss';

/**
 * DeleteConfirmModal – confirmation popover shown next to a scan card
 * before deleting a user scan.
 *
 * @param {boolean}  open      – whether the popover is visible
 * @param {Function} onClose   – callback to close without deleting (cancel / X / click-outside)
 * @param {Function} onConfirm – callback when the user confirms deletion
 * @param {string}   scanName  – name of the scan being deleted
 */
const DeleteConfirmModal = ({ open, onClose, onConfirm, scanName }) => {
  const localize = useLocalize();
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  // Ref callback – safe to recreate since the component unmounts when !open
  const positionRef = (el) => {
    if (!el) return;
    popoverRef.current = el;
    requestAnimationFrame(() => clampToViewport(el, { leftOffset: -48 }));
  };

  if (!open) return null;

  return (
    <div
      className="delete-confirm-popover"
      ref={positionRef}
      data-testid="delete-confirm-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="delete-confirm-header">
        <h2
          className="delete-confirm-title"
          id="delete-confirm-title"
        >
          {localize('desktop.scans.myscans.delete.title')}
        </h2>
        <button
          className="delete-confirm-close"
          onClick={onClose}
          data-testid="delete-confirm-close"
          aria-label="Close"
        >
          <CloseIcon sx={{ width: 12, height: 12 }} />
        </button>
      </div>

      <p className="delete-confirm-message">
        {localize('desktop.scans.myscans.delete.confirm', { scanName: scanName || '' })}
      </p>

      <div className="delete-confirm-actions">
        <button
          className="delete-confirm-cancel"
          onClick={onClose}
          data-testid="delete-confirm-cancel"
        >
          {localize('button.label.cancel')}
        </button>
        <button
          className="delete-confirm-execute"
          onClick={onConfirm}
          data-testid="delete-confirm-execute"
        >
          {localize('button.label.execute')}
        </button>
      </div>
    </div>
  );
};

DeleteConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  scanName: PropTypes.string
};

export default DeleteConfirmModal;
